/* Zivol Claim — option selection, variant resolution, size guide.
 *
 * WHAT THIS OWNS
 *   - Colour + size selection -> the correct variant id in the form's hidden
 *     input. This is the only thing on the page that can break a purchase, so
 *     it is written to fail visibly (button disabled) rather than silently
 *     post the wrong id.
 *   - Price and button label.
 *   - Telling the theme's gallery to move, via the same StandardEvents
 *     contract Grove uses.
 *   - The size guide dialog.
 *
 * WHAT IT DOES NOT OWN
 *   Nothing in the cart, nothing global, no Grove selectors.
 */
(() => {
  const root = document.querySelector('[data-claim-root]');
  if (!root) return;

  const dataEl = root.querySelector('[data-claim-variants]');
  if (!dataEl) return;

  let variants = [];
  try {
    variants = JSON.parse(dataEl.textContent || '[]');
  } catch {
    return; // malformed payload: leave the form exactly as the server rendered it
  }

  const idInput = root.querySelector('[data-claim-variant-id]');
  const buyBtn = root.querySelector('[data-claim-buy]');
  const priceEl = root.querySelector('[data-claim-price]');
  const wasEl = root.querySelector('[data-claim-was]');
  const buyPriceEl = root.querySelector('[data-claim-buy-price]');
  const colorOut = root.querySelector('[data-claim-color-out]');
  const sizeOut = root.querySelector('[data-claim-size-out]');

  const colorInputs = [...root.querySelectorAll('input[name="claim-color"]')];
  const sizeInputs = [...root.querySelectorAll('input[name="claim-size"]')];

  const selected = (list) => list.find((i) => i.checked);

  function find(color, size) {
    return variants.find((v) => v.color === color && v.size === size) || null;
  }

  /* Sizes that do not exist (or are sold out) for the chosen colour are
     disabled rather than hidden, so the row does not reflow under the thumb. */
  function reflectAvailability(color) {
    sizeInputs.forEach((input) => {
      const v = find(color, input.value);
      const usable = !!v && v.available;
      input.disabled = !usable;
      if (input.checked && !usable) input.checked = false;
    });
    if (!selected(sizeInputs)) {
      const firstUsable = sizeInputs.find((i) => !i.disabled);
      if (firstUsable) firstUsable.checked = true;
    }
  }

  function apply(dispatch) {
    const color = selected(colorInputs)?.value;
    const size = selected(sizeInputs)?.value;
    if (colorOut && color) colorOut.textContent = color;
    if (sizeOut && size) sizeOut.textContent = size;

    const v = color && size ? find(color, size) : null;

    if (!v || !v.available) {
      if (idInput) idInput.value = '';
      if (buyBtn) {
        buyBtn.disabled = true;
        buyBtn.textContent = v ? 'Sold out' : 'Unavailable';
      }
      return;
    }

    if (idInput) idInput.value = String(v.id);
    if (buyBtn) {
      buyBtn.disabled = false;
      buyBtn.innerHTML = '';
      buyBtn.append('Add to cart — ');
      const span = document.createElement('span');
      span.setAttribute('data-claim-buy-price', '');
      span.textContent = v.price;
      buyBtn.append(span);
    }
    if (priceEl) priceEl.textContent = v.price;
    if (wasEl) {
      // Hidden rather than emptied, so the line does not reflow when a variant
      // has no compare-at price.
      if (v.was) {
        wasEl.textContent = v.was;
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }
    if (dispatch) moveGallery(v.id, v.available);
  }

  /* Same contract Grove proved: product-form.js reads detail.resource.id and
     media-gallery.js reads detail.html, so BOTH must be present. Loaded
     dynamically so a missing import map degrades to "the image does not
     follow" instead of taking the selectors down with it. */
  /* media-gallery.js listens on its closest .shopify-section for a
     ProductSelectEvent and reads event.promise, which must resolve with
     { detail: { html, resource } }. Dispatching a plain CustomEvent on
     document does nothing — that was the first version of this and the
     gallery never moved. Mirrors the path proven on Grove.

     `resource` is NOT optional: product-form.js:746 does
     variantId.value = resource?.id ?? '', so omitting it wipes the hidden
     input the form posts. */
  /* The theme replaces the whole <media-gallery> element on variant select
     (media-gallery.js:52 `this.replaceWith(...)`), which is a hard cut. We cannot
     hook that swap without editing a shared theme file Grove also uses, so the
     fade is applied to the CONTAINER instead: fade out before dispatching, and
     fade back in when a new <media-gallery> is observed landing inside it.

     Timing is the theme's own — var(--animation-speed) / var(--animation-easing),
     0.125s ease-in-out — rather than a new duration invented for this page.

     Under prefers-reduced-motion the whole thing is skipped: no class is added,
     no observer runs, and the swap stays instant. */
  const galleryBox = root.querySelector('.claim-hero__gallery');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function fadeGalleryAcrossSwap() {
    if (!galleryBox || reduceMotion.matches) return;
    galleryBox.classList.add('is-swapping');

    const done = () => {
      galleryBox.classList.remove('is-swapping');
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (node.nodeType === 1 && node.tagName === 'MEDIA-GALLERY') {
            requestAnimationFrame(done);
            return;
          }
        }
      }
    });
    observer.observe(galleryBox, { childList: true, subtree: true });
    // If the fetch fails the gallery never swaps — never leave it faded out.
    const failsafe = window.setTimeout(done, 1200);
  }

  async function moveGallery(variantId, available) {
    fadeGalleryAcrossSwap();
    const section = root.closest('.shopify-section');
    const url = root.getAttribute('data-claim-product-url');
    const sectionId = root.getAttribute('data-claim-section-id');
    if (!section || !url || !sectionId || !variantId) return;

    try {
      const { ProductSelectEvent } = await import('@shopify/events');
      const { promise, resolve, reject } = ProductSelectEvent.createPromise();
      section.dispatchEvent(new ProductSelectEvent({ promise }));

      const fetchUrl = `${url}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;
      fetch(fetchUrl)
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((text) =>
          resolve({
            detail: {
              html: new DOMParser().parseFromString(text, 'text/html'),
              resource: { id: variantId, available: available !== false },
            },
          })
        )
        .catch(reject);
    } catch {
      /* importmap missing — leave the gallery alone rather than break the page */
    }
  }

  colorInputs.forEach((input) =>
    input.addEventListener('change', () => {
      reflectAvailability(input.value);
      apply(true);
    })
  );
  sizeInputs.forEach((input) => input.addEventListener('change', () => apply(true)));

  /* Honest pending state: the button says what is happening and stops a second
     submit. It never claims success — the drawer does that. */
  const form = root.querySelector('form[action*="/cart/add"]');
  if (form && buyBtn) {
    form.addEventListener('submit', () => {
      buyBtn.setAttribute('aria-busy', 'true');
      window.setTimeout(() => buyBtn.removeAttribute('aria-busy'), 6000);
    });
  }

  /* Orbit video.

     preload="none" means nothing is fetched until we ask, so the poster carries
     the whole visual until the section is actually approached. The observer
     calls load() then play() once; play() is promise-based and can reject (some
     browsers refuse even muted autoplay), so a rejection falls back to showing
     the play control rather than leaving a dead frame.

     Under prefers-reduced-motion nothing autoplays: the poster holds and the
     control is the only way to start it. */
  const videoBox = root.ownerDocument.querySelector('[data-claim-video]');
  if (videoBox) {
    const vid = videoBox.querySelector('[data-claim-video-el]');
    const playBtn = videoBox.querySelector('[data-claim-video-play]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const start = () => {
      if (!vid) return;
      vid.load();
      const p = vid.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { if (playBtn) playBtn.hidden = true; }).catch(() => {
          if (playBtn) playBtn.hidden = false;
        });
      }
    };

    playBtn?.addEventListener('click', () => {
      vid?.setAttribute('controls', '');
      start();
    });

    if (!reduce.matches && vid && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              start();
              io.disconnect();
              return;
            }
          }
        },
        { rootMargin: '0px 0px 200px 0px', threshold: 0.25 }
      );
      io.observe(videoBox);
    }
  }

  /* Size guide */
  const dialog = root.querySelector('[data-claim-sizeguide]');
  const openBtn = root.querySelector('[data-claim-sizeguide-open]');
  const closeBtn = root.querySelector('[data-claim-sizeguide-close]');
  if (dialog && openBtn && typeof dialog.showModal === 'function') {
    openBtn.addEventListener('click', () => dialog.showModal());
    closeBtn?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
  } else if (openBtn && dialog) {
    // No <dialog> support: leave the table permanently visible rather than
    // hiding measurements behind a control that cannot open.
    openBtn.hidden = true;
    dialog.setAttribute('open', '');
  }

  /* Park the chat launcher while the buy column is on screen.

     Hiding rather than moving, because the launcher is inside a shadow root and
     will always sit at the bottom of the viewport — when the buy column grew by
     a price line it just found something else to cover. `:not([open])` in the
     CSS means an open conversation is never hidden.

     If IntersectionObserver is unavailable the class is never set and the
     launcher behaves exactly as it does today. */
  const buyCol = root.querySelector('.claim-hero__buy');
  if (buyCol && 'IntersectionObserver' in window) {
    const park = (on) => document.body.classList.toggle('claim-chat-parked', on);
    park(true); // buy column is on screen at load
    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) park(entry.isIntersecting);
      },
      /* Positive bottom margin, not negative. A negative margin shrank the root
         and reported "not intersecting" while the title was already under the
         launcher. Plain intersection was still late: at scroll 0 the buy column
         only just clears the fold, so it counted as absent while the launcher
         already overlapped its top edge.

         Expanding the root 400px downward parks the launcher from the moment
         the hero is on screen and holds it until the buyer is genuinely past
         the buy column. */
      { rootMargin: '0px 0px 400px 0px', threshold: 0 }
    ).observe(buyCol);
  }

  const startColor = selected(colorInputs)?.value;
  if (startColor) reflectAvailability(startColor);
  apply(false);
})();
